import { Fragment, type ReactNode } from "react";

/**
 * A search result's `context` is plain text with matches wrapped in `<b>`, and
 * every `<`, `>` and `&` in the document already escaped
 * (`docs/spec/search.md` §6). It is "safe to render as HTML" — but we do not
 * render it as HTML: we parse out the only tag it is allowed to contain and
 * build elements, so nothing the server (or a document's author) writes can
 * ever reach `innerHTML`.
 */

const ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
  "&nbsp;": " ",
};

export function unescapeEntities(value: string): string {
  return value.replace(/&(?:amp|lt|gt|quot|apos|nbsp|#39);/gu, (match) => ENTITIES[match] ?? match);
}

export interface SnippetPart {
  text: string;
  bold: boolean;
}

/** Splits a snippet into runs, keeping only `<b>` as a signal. */
export function parseSnippet(context: string): SnippetPart[] {
  const parts: SnippetPart[] = [];
  let bold = false;
  for (const piece of context.split(/(<b>|<\/b>)/u)) {
    if (piece === "<b>") {
      bold = true;
      continue;
    }
    if (piece === "</b>") {
      bold = false;
      continue;
    }
    if (piece === "") continue;
    parts.push({ text: unescapeEntities(piece), bold });
  }
  return parts;
}

export function Snippet({ context }: { context: string }): ReactNode {
  const parts = parseSnippet(context);
  if (parts.length === 0) return null;
  return (
    <p className="mt-1 text-sm whitespace-pre-wrap text-muted">
      {parts.map((part, index) => (
        <Fragment key={`${String(index)}:${part.text}`}>
          {part.bold ? <b className="font-semibold text-ink">{part.text}</b> : part.text}
        </Fragment>
      ))}
    </p>
  );
}
